import { Injectable } from '@angular/core';
import { Validators, ValidatorFn, FormControl, FormGroup, FormBuilder, AbstractControl } from '@angular/forms';
import { Observable, forkJoin, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';


import { TranslateService } from '@ngx-translate/core';
import { FieldsContract, FormsContract, ValidationsContract } from 'src/app/graphQL/models/config/configContract.model';

@Injectable({
  providedIn: 'root'
})
export class SharedFormValidationsService {
  messageService: any;
  errorMessagesMap: any | undefined;

  constructor(
    private fb: FormBuilder
  ) { }

  /**
   * Maps validations from database to Angular validators.
   * @param validations - Array of validation objects from the database.
   * @returns Array of Angular validators.
   */
  public mapValidationsToValidators(fields: FieldsContract[]): { validators: ValidatorFn[], errorMessages: { [key: string]: string } } {
    const validators: ValidatorFn[] = [];
    const errorMessages: { [key: string]: string } = {};

    fields.forEach(field => {
      field.validations?.forEach((validation: ValidationsContract) => {
        switch (validation.type) {
          case 'required':
            if (validation.value === 'true') {
              validators.push(Validators.required);
              errorMessages['Required'] = validation.errorMessage || 'This field is required';
            }
            break;
          case 'MinLength':
            validators.push(Validators.minLength(parseInt(validation.value, 10)));
            errorMessages['MinLength'] = this.formatString(validation.errorMessage, field.fieldDescription, validation.value);
            break;
          case 'MaxLength':
            validators.push(Validators.maxLength(parseInt(validation.value, 10)));
            errorMessages['MaxLength'] = this.formatString(validation.errorMessage, field.fieldDescription, validation.value);
            break;
          case 'Regex':
            validators.push(Validators.pattern(validation.value));
            errorMessages['Regex'] = this.formatString(validation.errorMessage, field.fieldDescription);
            break;
          case 'MinValue':
            validators.push(Validators.min(parseInt(validation.value, 10)));
            errorMessages['MinValue'] = this.formatString(validation.errorMessage, field.fieldDescription);
            break;
          case 'MaxValue':
            validators.push(Validators.min(parseInt(validation.value, 10)));
            errorMessages['MaxValue'] = this.formatString(validation.errorMessage, field.fieldDescription);
            break;
          default:
            break;
        }
      });
    });
    return { validators, errorMessages };
  }

  /**
   * Retrieves all validations for a given dataForm and returns an observable of the form group.
   * @param dataForm - The data form object.
   * @returns Observable of FormGroup.
   */
  public getValidationsForForm(dataForm: FormsContract): Observable<{ formGroup: FormGroup, errorMessagesMap: { [key: string]: { [key: string]: string } }, fileNameLabelMap: { [key: string]: string } }> {
    return of(dataForm.fields || []).pipe(
        switchMap(fields => {
          if (!Array.isArray(fields) || fields.length === 0) {
            return of([]);
          }
          const fileNameLabel: { [key: string]: string } = {};

          const validationRequests = fields
            .filter((field: FieldsContract) => field.isActive)
            .map((field: FieldsContract) => {
              const { validators, errorMessages } = this.mapValidationsToValidators([field]);

          if (field.isMandatory) {
            validators.push(Validators.required);

            fileNameLabel[field.fieldName] = `${field.fieldDescription} *`;
          } else {
            fileNameLabel[field.fieldName] = field.fieldDescription;
          }

          if (field.fieldType === 'form') {
              return of({
                  fieldName: field.fieldName,
                  control: this.fb.array([]),
                  errorMessages: errorMessages,
                  fileNameLabel: fileNameLabel
              });
          } else {
              return of({
                  fieldName: field.fieldName,
                  control: new FormControl('', validators),
                  errorMessages: errorMessages,
                  fileNameLabel: fileNameLabel
              });
          }
        });
        return forkJoin(validationRequests);
      }),
      map(fieldsWithControls => {
            const formGroup = new FormGroup({});
            const errorMessagesMap: { [key: string]: { [key: string]: string } } = {};
            const fileNameLabelMap: { [key: string]: string } = {};

            fieldsWithControls.forEach(({ fieldName, control, errorMessages, fileNameLabel }) => {
                formGroup.addControl(fieldName, control);
                errorMessagesMap[fieldName] = errorMessages;
                Object.assign(fileNameLabelMap, fileNameLabel);
            });

            return { formGroup, errorMessagesMap, fileNameLabelMap };
        })
    );
  }


  /**
   * Formats a string by replacing placeholders with values.
   * @param str - The string to format.
   * @param values - The values to replace the placeholders with.
   * @returns The formatted string.
   */
  formatString(str: string, ...values: any[]): string {
    return str.replace(/{(\d+)}/g, function(match, index) {
      return typeof values[index] != 'undefined'
        ? values[index]
        : match
      ;
    });
  }
  /**
  * Retrieves the error messages for a specific form control.
  * @param form - The form group.
  * @param controlName - The name of the form control.
  * @param errorKey - The key of the error message.
  * @param errorMessagesMap - The map of error messages.
  * @returns An array of error messages for the specified control.
  */
  getErrorMessages(form: FormGroup, controlName: string, errorKey: string, errorMessagesMap: {[key: string]: {[key: string]: string}}): string[] {
    const controlErrors = form.controls[controlName]?.errors;
    if (!controlErrors) {
      return [];
    }
    return [errorMessagesMap[controlName][errorKey]];
  }

  /**
  * Handles the blur event for a form control.
  * Marks the control as touched.
  * @param controlName - The name of the form control.
  */
  onBlur(form: FormGroup, controlName: string): void {
    const control = form.get(controlName);
    if (control) {
      control.markAsTouched();
    }
  }

  /**
   * Retrieves the label for a specific form control.
   * @param form - The form group.
   * @param controlName - The name of the form control.
   * @param errorKey - The key of the error message.
   * @param errorMessagesMap - The map of error messages.
   * @returns The label for the specified control.
   */
  getFieldLabel(form: FormGroup, controlName: string, fileNameLabelMap: { [key: string]: string }): string {
    const controlErrors = form.controls[controlName];
    if (!controlErrors) {
      return ""
    }
    return fileNameLabelMap[controlName];
  }

  /**
  * Formats the input value to uppercase.
  *
  * @private
  * @param {any} event - The input event.
  * @return {string} - The formatted input value.
  */
  formatInputToUpper(event: any) : string {
    const inputValue = event.target.value.toUpperCase();
    return inputValue;
  }

  /**
   * Copia el estado de un control de formulario a otro.
   *
   * @param sourceControl - El control de formulario fuente del cual se copiará el estado.
   * @param targetControl - El control de formulario destino al cual se copiará el estado.
   */

  public copyControlState(sourceControl: AbstractControl, targetControl: AbstractControl): void {
      if (sourceControl.touched) {
          targetControl.markAsTouched();
      } else {
          targetControl.markAsUntouched();
      }

      if (sourceControl.dirty) {
          targetControl.markAsDirty();
      } else {
          targetControl.markAsPristine();
      }

      if (sourceControl.disabled) {
          targetControl.disable({ emitEvent: false });
      } else {
          targetControl.enable({ emitEvent: false });
      }

      targetControl.setErrors(sourceControl.errors, { emitEvent: false });
  }
}
